import { useLayoutEffect, useState } from 'react';
import { Form as AntForm, Modal, Upload } from 'antd';
import { AnySchema } from 'yup';
import styled from 'styled-components';
import ReactCropper from 'react-cropper';
import { PlusOutlined } from '@ant-design/icons';
import { formHelpers } from '@helpers/formHelpers';
import { readFileAsync } from '@lib/files';
import { UseFormMethods } from 'react-hook-form';

const { Item: FormItem } = AntForm;

const HSMSCropUploadWrapper = styled.div`
  .form-item-label {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .ant-upload.ant-upload-select-picture-card {
    position: relative;
    padding: 2.1rem 2rem;
  }

  .ant-upload.ant-upload-select-picture-card {
    position: relative;
    border-radius: 5px;
  }

  .image-preview {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    border-radius: 5px;
    padding: 2px;
    display: flex;
    justify-content: center;
  }
`;

interface HSMSCropUploadProps {
  label: string;
  name: string;
  setValue: UseFormMethods['setValue'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues: (payload?: { nest: boolean }) => any;
  register: (ref: Element | null) => void;
  accept?: string;
  validationSchema?: AnySchema;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: any; // If we are setting proper types we need to get the form values. Not possible since we use this in multiple forms
}

export const HSMSCropUpload = ({
  label,
  name,
  setValue,
  getValues,
  register,
  accept,
  validationSchema,
  errors,
}: HSMSCropUploadProps) => {
  const [isCropperOpen, setIsCropperOpen] = useState<boolean>(false);
  const [fileToCrop, setFileToCrop] = useState<string | ArrayBuffer | null>('');
  const [cropper, setCropper] = useState<ReactCropper | null>(null);
  const [fieldValues, setFieldValues] = useState<string>();

  useLayoutEffect(() => {
    if (!fieldValues) {
      setFieldValues(getValues()[name]);
    }
  });

  const value = getValues()[name];
  return (
    <HSMSCropUploadWrapper>
      <FormItem
        className="form-item-label"
        label={label}
        required={validationSchema && formHelpers.isRequired(validationSchema, name)}
        validateStatus={errors && errors[name] ? 'error' : ''}
        help={errors && errors[name]?.message}
        labelCol={{ span: 24 }}
      >
        {/* This is to register a field with the given name in react-hook-form */}
        <input type="text" hidden name={name} ref={register} />

        <Upload
          accept={accept}
          name="avatar"
          listType="picture-card"
          className="avatar-uploader"
          showUploadList={false}
          customRequest={async (file) => {
            // Convert the file into a dataURL to be fed into the cropper
            const fileURL = await readFileAsync(file.file as File);
            setFileToCrop(fileURL);

            // Open the cropper
            setIsCropperOpen(true);
          }}
        >
          <div>
            {value && <img className="image-preview" src={value} alt="Preview" />}
            <PlusOutlined />
            <div className="ant-upload-text">Upload</div>
          </div>
        </Upload>

        <Modal
          title="Crop the image"
          visible={isCropperOpen}
          onOk={() => {
            if (cropper) {
              // Set the form value to the cropped image blob
              setValue(name, cropper.getCroppedCanvas().toDataURL('image/jpeg', 0.95));
            }

            setIsCropperOpen(false);
          }}
          onCancel={() => setIsCropperOpen(false)}
        >
          <ReactCropper
            ref={(ref) => setCropper(ref)}
            src={fileToCrop?.toString()}
            style={{ height: '400px', width: '100%' }}
            aspectRatio={1}
            viewMode={3}
            guides={false}
          />
        </Modal>
      </FormItem>
    </HSMSCropUploadWrapper>
  );
};
